<script>
    // -- IMPORTS
    
    import { languageArrayStore } from "$src/lib/store/languageArrayStore";
    import { onMount } from "svelte";
    import FileInput from "../element/FileInput/FileInput.svelte";
    import InputLocalizedForm from "../element/InputLocalizedForm.svelte";
    import Input from "../element/Input.svelte";
    import { addFileArrayOnObject, fetchData } from '../../base';
    import { toast } from "../../toast";
    import { isLoadingStore } from "../../store/isLoadingStore";
    import { getJsonText } from "senselogic-gist";

    // -- VARIABLES

    let 
    {     
        card = $bindable(
            {
                id : '',
                slug : 'new',
                typeSlug : 'card-group',
                sectionPositionNumber: 0,
                number : 0,
                title : null,
                text : null,
                textArray: [],
                mediaPath : null,
                mediaPathArray : [],
                imageFit : null,
                imageHorizontalPosition : null,
                imageVerticalPosition : null,
                imageSide: 'right',
            }
        ),
        number = 0
    } = $props();
    
    let fileArray = $state([]);
    let folderPath = 'block/'
                      + card?.slug?.toLowerCase().replaceAll("-", "_")
                      + '/';
    let form = $state();

    let isSubmitting = false;

    // -- FUNCTIONS

    function checkFieldByType(
        blockForm
        )
    {
        let haveNoTitleOrText = !blockForm.title 
                                && !blockForm.text;

        let hasCTAIncomplete = ( blockForm.teaser && !blockForm.href ) 
                               || ( blockForm.href && !blockForm.teaser );


        if( blockForm.slug === 'new' )
        {
            toast.error( 'Please add descriptive slug' );

            return true;
        }

        if( haveNoTitleOrText )
        {
            toast.error( 'Please add a title or a text, or change for image on card ' );

            return true;
        }
        else if( hasCTAIncomplete )
        {
            toast.error( 'Please add the CTA and the destination on card ' );

            return true;
        }
    }

    // ~~

    async function handleSubmit( 
        event 
        )
    {
        event.preventDefault();

        $isLoadingStore = true;

        try
        {
            if ( !event )
            {
                return;
            }

            let formData = new FormData();
            
            card = addFileArrayOnObject( card, fileArray );

            let hasError = checkFieldByType( card );

            if( hasError )
            {
                $isLoadingStore = false;

                return;
            }

            for ( let [ key, value ] of Object.entries( card ) )
            {
                if ( key.includes( 'Array' ) 
                     || Array.isArray( value ) )
                {
                    formData.append( key, getJsonText( value ) )
                }
                else
                {
                    formData.append( key, value )
                }
            }

            let response =
                await fetchData(
                    '/api/block/add',
                    {
                        method: 'POST',
                        body: formData,
                        credentials: 'include',
                    }
                    );

            if ( response.error )
            {
                toast(
                    {
                        variant: 'error',
                        text: 'Something went wrong'
                    }
                    );
            }
            else if( response.authError )
            {
                toast(
                    {
                        variant: 'error',
                        text: response.authError
                    }
                    );
            }
            else
            {
                toast(
                    {
                        variant: 'success',
                        text: response.message
                    }
                    );
            }
        }
        catch ( error )
        {
            toast(
                {
                    variant: 'error',
                    text: 'Error saving your block'
                }
                );
            console.log( error );
        }
        finally
        {
            $isLoadingStore = false;
            setDefaultFileArray();
        }
    }

    // ~~

    function handleUpdateLocalizedInput(
        event
        )
    {
        card[ event.detail.name ] = event.detail.text;
    }

    // ~~

    function setDefaultFileArray(
        )
    {
        if ( card?.mediaPath !== null 
             && card?.mediaPath !== '' )
        {
            fileArray = [ card.mediaPath ];
        }
        else
        {
            fileArray = card.mediaPathArray ?? [];
        }
    }

    // ~~

    function customSubmit(
        event
        )
    {
        if ( form )
        {
            let fakeSubmitEvent = new Event('submit', { bubbles: true, cancelable: true } );
            form.dispatchEvent( fakeSubmitEvent );
        }
    }

    // -- STATEMENTS

    onMount( 
        () =>
        {
            setDefaultFileArray();

            window.addEventListener(
                'on-save-block',
                customSubmit
                );
            isSubmitting

            return () => {
                    window.removeEventListener( 'on-save-block', customSubmit );
                }
        }
    );

    //~~ 

    $effect(
        () =>
        {
            addFileArrayOnObject( card, fileArray )
        }
    );
</script>

<style lang="stylus">
    // -- ELEMENTS

    form
    {
        padding: 2rem;
    }

    // -- CLASSES

    .block-section
    {
        display: flex;
        flex-direction: column;
        gap: 2rem;
    }
</style>

<form
    onsubmit={ handleSubmit }
    bind:this={ form }
>
    <div class="block-section">
        <Input
            label={ 'Position in section' }
            id="number"
            type="number"
            helperText="This number is used to sort inside the section"
            fullWidth
            bind:value={ card.sectionPositionNumber }
        />

        <InputLocalizedForm
            itemsString={ card.title }
            languageArray={ $languageArrayStore }
            name={ 'title' }
            onUpdate={ handleUpdateLocalizedInput }
            placeholder={ 'title' }
        />
        
        <InputLocalizedForm
            isMultiLine
            itemsString={ card.text }
            languageArray={ $languageArrayStore }
            name={ 'text' }
            onUpdate={ handleUpdateLocalizedInput }
            placeholder={ 'text' }
        />
        
        <InputLocalizedForm
            itemsString={ card.teaser }
            languageArray={ $languageArrayStore }
            name={ 'teaser' }
            onUpdate={ handleUpdateLocalizedInput }
            placeholder={ 'Call to action' }
        />

        <InputLocalizedForm
            itemsString={ card.href }
            languageArray={ $languageArrayStore }
            name={ 'href' }
            onUpdate={ handleUpdateLocalizedInput }
            placeholder={ 'Destination' }
        />
    </div>

    <div class="block-section">
        <h2>Media</h2>
        <FileInput
            bind:fileArray={ fileArray }
            fileInputName="image-path"
            acceptedType="image/*, video/*"
            maxFileCount={ 2 }
            filePath={ folderPath }
        />
    </div>
</form>